import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { formatDistanceToNowStrict, format } from 'date-fns';
import { Plus, Search, SlidersHorizontal, Pencil, KeyRound, UserX, UserCheck, Trash2, Phone, Eye, EyeOff } from 'lucide-react';
import { userAPI, roleAPI, logsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { AppSheet, SheetRow } from '../common/AppViewKit';
import { Switch, FullSheet, ConfirmSheet, Chip, UsersTabs, fabStyle, sectionLabel, groupBox } from './kit';
import {
  SITE_ROLE_ELIGIBLE, SECONDARY_ROLE_OPTIONS, ROLE_LABEL, ROLE_HELP, saveUser, grantedFieldRights, filterUsers, countRights,
} from '../../utils/userAccess';

const ROLES = ['admin', 'co-admin', 'staff', 'agent', 'technical'];
const errText = (err) => {
  const m = err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || err.message || 'Failed';
  return /E11000|duplicate key/i.test(m) ? 'That mobile number is already used by another user.' : m;
};
const ago = (d) => (d ? `${formatDistanceToNowStrict(new Date(d))} ago` : 'Never');

function Avatar({ name, active = true, size = 40 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: size / 2, flexShrink: 0, display: 'grid', placeItems: 'center', fontWeight: 700,
      background: active ? 'var(--primary, #2563eb)' : 'var(--border)', color: active ? '#fff' : 'var(--text-muted)', fontSize: size * .4,
    }}>{(name || '?')[0].toUpperCase()}</div>
  );
}
const RoleTag = ({ role }) => (
  <span className={`badge ${role === 'admin' ? 'badge-danger' : role === 'agent' ? 'badge-warning' : role === 'technical' ? 'badge-success' : role === 'co-admin' ? 'badge-catv' : 'badge-info'}`}
    style={{ fontSize: '.68rem' }}>{ROLE_LABEL[role] || role}</span>
);

// ── Add / edit form ─────────────────────────────────────────────────────────
function UserForm({ user, me, roles, onClose, onSaved, askConfirm }) {
  const isSelf = !!user && user._id === me?._id;
  const [form, setForm] = useState(user
    ? { ...user, siteRoleId: user.siteRole?._id || user.siteRole || '', secondaryRoles: user.secondaryRoles || [] }
    : { name: '', mobile: '', email: '', role: 'staff', password: '', siteRoleId: '', secondaryRoles: [] });
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [fieldModules, setFieldModules] = useState([]);
  const [fieldRights, setFieldRights] = useState({});
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: undefined })); };

  useEffect(() => {
    userAPI.getModuleRegistry().then(res => {
      const modules = (res.data.data || []).filter(m => m.section === 'FIELD AGENT APP');
      setFieldModules(modules);
      setFieldRights(Object.fromEntries(modules.map(m => [m.key,
        Object.fromEntries(m.actions.map(a => [a.key, !!user?.fieldRights?.[m.key]?.[a.key]]))])));
    }).catch(() => {});
  }, [user]);

  const effective = [form.role, ...form.secondaryRoles];
  const showAgent = effective.includes('agent');
  const showSiteRole = effective.some(r => SITE_ROLE_ELIGIBLE.includes(r) && r !== 'agent');
  const role = roles.find(r => r._id === form.siteRoleId);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Enter the full name.';
    const digits = (form.mobile || '').replace(/\D/g, '');
    if (!digits) e.mobile = 'Enter the mobile number.';
    else if (digits.length < 10 && (!user || form.mobile !== user.mobile)) e.mobile = 'A mobile number has 10 digits.';
    if (!user && (form.password || '').length < 6) e.password = 'Password needs at least 6 characters.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const doSave = async () => {
    setSaving(true);
    try {
      await saveUser({ user, form: { ...form, name: form.name.trim(), mobile: form.mobile.trim() }, fieldRights });
      toast.success(user ? 'User updated' : 'User created');
      onSaved();
    } catch (err) { toast.error(errText(err)); }
    finally { setSaving(false); }
  };

  const submit = () => {
    if (!validate()) return;
    const makesAdmin = form.role === 'admin' && user?.role !== 'admin';
    if (makesAdmin) {
      askConfirm({
        title: 'Give full admin access?', danger: true, yes: 'Yes, make admin',
        body: `${form.name || 'This user'} will be able to see and change everything, including other users' access. Site Roles do not limit an admin.`,
        onYes: doSave,
      });
    } else doSave();
  };

  const field = (label, err, input) => (
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
      <label style={{ display: 'block', fontSize: '.74rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</label>
      {input}
      {err && <div role="alert" style={{ color: 'var(--danger, #dc2626)', fontSize: '.78rem', marginTop: 4 }}>{err}</div>}
    </div>
  );
  const inputStyle = { width: '100%', border: 'none', background: 'none', padding: 0, outline: 'none', color: 'var(--text)', minHeight: 28 };

  return (
    <FullSheet title={user ? `Edit ${user.name}` : 'Add user'} onClose={onClose}
      footer={<button type="button" className="btn btn-primary" style={{ width: '100%', minHeight: 48 }} disabled={saving} onClick={submit}>{saving ? 'Saving…' : user ? 'Save changes' : 'Create user'}</button>}>
      <div style={{ ...sectionLabel, marginTop: 2 }}>Details</div>
      <div style={groupBox}>
        {field('Full name', errors.name, <input style={inputStyle} value={form.name} autoComplete="off" onChange={e => set('name', e.target.value)} />)}
        {field('Mobile (used to log in)', errors.mobile, <input style={inputStyle} inputMode="tel" value={form.mobile} autoComplete="off" onChange={e => set('mobile', e.target.value)} />)}
        {!user && field('Password', errors.password, (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <input style={inputStyle} type={showPw ? 'text' : 'password'} value={form.password || ''} placeholder="At least 6 characters" autoComplete="new-password" onChange={e => set('password', e.target.value)} />
            <button type="button" aria-label={showPw ? 'Hide password' : 'Show password'} onClick={() => setShowPw(s => !s)} style={{ border: 'none', background: 'none', color: 'var(--text-muted)', width: 40, height: 32 }}>
              {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        ))}
      </div>
      {user && <div className="text-muted text-small" style={{ margin: '6px 4px' }}>To change the password, use Reset password on the user's profile.</div>}

      <div style={sectionLabel}>Role</div>
      <div style={groupBox}>
        {field('Main role', null, (
          <select style={{ ...inputStyle, minHeight: 32 }} value={form.role} disabled={isSelf}
            onChange={e => setForm(f => ({ ...f, role: e.target.value, secondaryRoles: f.secondaryRoles.filter(r => r !== e.target.value) }))}>
            {ROLES.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
          </select>
        ))}
        <div style={{ padding: '8px 14px', fontSize: '.78rem', color: 'var(--text-muted)' }}>
          {isSelf ? 'You cannot change your own role.' : ROLE_HELP[form.role]}
        </div>
      </div>

      {form.role !== 'admin' && (
        <>
          <div style={sectionLabel}>Also log in as</div>
          <div style={groupBox}>
            {SECONDARY_ROLE_OPTIONS.filter(r => r !== form.role).map(r => (
              <div key={r} style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 52, padding: '0 14px', borderBottom: '1px solid var(--border)' }}>
                <span style={{ flex: 1, fontWeight: 600, fontSize: '.9rem' }}>{ROLE_LABEL[r]} app</span>
                <Switch label={`Also log in as ${ROLE_LABEL[r]}`} disabled={isSelf} checked={form.secondaryRoles.includes(r)}
                  onChange={() => set('secondaryRoles', form.secondaryRoles.includes(r) ? form.secondaryRoles.filter(x => x !== r) : [...form.secondaryRoles, r])} />
              </div>
            ))}
          </div>
        </>
      )}

      {showSiteRole && (
        <>
          <div style={sectionLabel}>Site Role (what they can do)</div>
          <div style={groupBox}>
            {field('Assigned role', null, (
              <select style={{ ...inputStyle, minHeight: 32 }} value={form.siteRoleId} onChange={e => set('siteRoleId', e.target.value)}>
                <option value="">None, no access</option>
                {roles.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
              </select>
            ))}
            <div style={{ padding: '8px 14px', fontSize: '.78rem', color: form.siteRoleId ? 'var(--text-muted)' : 'var(--danger, #dc2626)' }}>
              {form.siteRoleId
                ? (role?.description || 'Rights are inherited from this role. Edit the role under Roles to change them for everyone who has it.')
                : 'With no role this login can sign in but cannot open anything.'}
            </div>
          </div>
        </>
      )}

      {showAgent && (
        <>
          <div style={sectionLabel}>Field rights (set for this agent only)</div>
          <div style={groupBox}>
            {fieldModules.map(mod => {
              const action = mod.actions[0].key;
              return (
                <div key={mod.key} style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 52, padding: '0 14px', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ flex: 1, fontSize: '.9rem' }}>{mod.label.replace(' (Field)', '')}</span>
                  <Switch label={mod.label} checked={!!fieldRights[mod.key]?.[action]}
                    onChange={() => setFieldRights(r => ({ ...r, [mod.key]: { ...r[mod.key], [action]: !r[mod.key][action] } }))} />
                </div>
              );
            })}
          </div>
        </>
      )}
    </FullSheet>
  );
}

// ── Reset password ──────────────────────────────────────────────────────────
function PasswordSheet({ user, onClose }) {
  const [pw, setPw] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const bad = pw.length > 0 && pw.length < 6;
  const save = async () => {
    setBusy(true);
    try {
      await userAPI.update(user._id, { password: pw });
      toast.success('Password changed. They will be signed out.');
      onClose();
    } catch (err) { toast.error(errText(err)); }
    finally { setBusy(false); }
  };
  return (
    <AppSheet title={`Reset password for ${user.name}`} onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--border)', borderRadius: 10, padding: '0 6px 0 12px' }}>
        <input type={show ? 'text' : 'password'} value={pw} onChange={e => setPw(e.target.value)} placeholder="New password" autoComplete="new-password"
          style={{ flex: 1, border: 'none', background: 'none', outline: 'none', color: 'var(--text)', minHeight: 48 }} />
        <button type="button" aria-label={show ? 'Hide password' : 'Show password'} onClick={() => setShow(s => !s)} style={{ border: 'none', background: 'none', color: 'var(--text-muted)', width: 44, height: 44 }}>
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
      <div style={{ fontSize: '.78rem', margin: '6px 2px 14px', color: bad ? 'var(--danger, #dc2626)' : 'var(--text-muted)' }}>
        {bad ? 'Password needs at least 6 characters.' : 'Share it with them yourself. Their current sessions are signed out.'}
      </div>
      <button type="button" className="btn btn-primary" style={{ width: '100%', minHeight: 48 }} disabled={busy || pw.length < 6} onClick={save}>{busy ? 'Saving…' : 'Change password'}</button>
    </AppSheet>
  );
}

// ── Change history rows (used by profile and Activity tab) ──────────────────
const show = (v) => (v === null || v === undefined || v === '' ? '(none)' : typeof v === 'object' ? JSON.stringify(v) : String(v));
function LogRow({ l, showTarget }) {
  const what = l.action === 'create' ? 'Created'
    : l.action === 'delete' ? 'Deleted'
      : `${l.field || 'Changed'}: ${show(l.oldValue)} → ${show(l.newValue)}`;
  return (
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
      {showTarget && <div style={{ fontWeight: 700, fontSize: '.86rem' }}>{l.customerName || '(unnamed)'} <span className="text-muted" style={{ fontWeight: 500, fontSize: '.74rem' }}>{l.entity === 'role' ? 'role' : 'user'}</span></div>}
      <div style={{ fontSize: '.84rem', overflowWrap: 'anywhere' }}>{what}</div>
      <div className="text-muted" style={{ fontSize: '.72rem', marginTop: 2 }}>
        {l.changedByName || 'System'} · {format(new Date(l.createdAt), 'dd MMM, HH:mm')}
      </div>
    </div>
  );
}

// ── Profile ─────────────────────────────────────────────────────────────────
function Profile({ user, me, roles, registry, onClose, onChanged, askConfirm }) {
  const navigate = useNavigate();
  const [logs, setLogs] = useState(null);
  const [pwOpen, setPwOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const isSelf = user._id === me?._id;
  const isAdmin = user.role === 'admin';
  const siteRole = roles.find(r => r._id === (user.siteRole?._id || user.siteRole));
  const usesSiteRole = [user.role, ...(user.secondaryRoles || [])].some(r => SITE_ROLE_ELIGIBLE.includes(r) && r !== 'agent');
  const usesField = [user.role, ...(user.secondaryRoles || [])].includes('agent');
  const rc = siteRole ? countRights(siteRole.rights, registry) : null;

  useEffect(() => {
    logsAPI.getAll({ entity: 'user', recordId: user._id, limit: 6 }).then(r => setLogs(r.data.data)).catch(() => setLogs([]));
  }, [user._id]);

  const run = (fn, ok) => async () => {
    try { await fn(); toast.success(ok); onChanged(); } catch (err) { toast.error(errText(err)); }
  };
  const deactivate = () => askConfirm({
    title: `Deactivate ${user.name}?`, danger: true, yes: isAdmin ? 'Yes, deactivate admin' : 'Deactivate',
    body: `${isAdmin ? 'This is an ADMINISTRATOR account.\n' : ''}They will not be able to sign in. Their payment and customer history stays. You can activate them again later.`,
    onYes: run(() => userAPI.delete(user._id), 'User deactivated'),
  });
  const activate = () => askConfirm({
    title: `Activate ${user.name}?`, yes: 'Activate', body: 'They will be able to sign in again with the same access as before.',
    onYes: run(() => userAPI.activate(user._id), 'User activated'),
  });
  const remove = () => askConfirm({
    title: `Permanently delete ${user.name}?`, danger: true, yes: 'Delete forever',
    body: 'This cannot be undone. It is refused if they have any payment or customer history. Deactivate instead in that case.',
    onYes: run(() => userAPI.deletePermanent(user._id), `${user.name} deleted`),
  });

  if (editOpen) return <UserForm user={user} me={me} roles={roles} askConfirm={askConfirm} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); onChanged(); }} />;
  if (pwOpen) return <PasswordSheet user={user} onClose={() => setPwOpen(false)} />;

  const row = (label, value) => (
    <div style={{ display: 'flex', gap: 12, padding: '9px 14px', borderBottom: '1px solid var(--border)', fontSize: '.86rem' }}>
      <span style={{ width: 96, flexShrink: 0, color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{value}</span>
    </div>
  );

  return (
    <AppSheet title="User" onClose={onClose}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <Avatar name={user.name} active={user.isActive} size={52} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '1.05rem', overflowWrap: 'anywhere' }}>{user.name}{isSelf && <span className="text-muted" style={{ fontWeight: 500 }}> (you)</span>}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
            <RoleTag role={user.role} />
            <span className={`badge badge-${user.isActive ? 'success' : 'warning'}`} style={{ fontSize: '.68rem' }}>{user.isActive ? 'Active' : 'Inactive'}</span>
          </div>
        </div>
        {user.mobile && (
          <a href={`tel:${user.mobile}`} aria-label="Call" style={{ width: 44, height: 44, display: 'grid', placeItems: 'center', borderRadius: 22, border: '1px solid var(--border)', color: 'var(--text)' }}><Phone size={18} /></a>
        )}
      </div>

      <div style={groupBox}>
        {row('Mobile', user.mobile)}
        {user.email && row('Email', user.email)}
        {row('Last login', user.lastLogin ? `${ago(user.lastLogin)} (${format(new Date(user.lastLogin), 'dd MMM yyyy, HH:mm')})` : 'Never')}
        {user.createdAt && row('Created', format(new Date(user.createdAt), 'dd MMM yyyy'))}
        {(user.secondaryRoles || []).length > 0 && row('Also logs in as', user.secondaryRoles.map(r => ROLE_LABEL[r]).join(', '))}
      </div>

      <div style={sectionLabel}>Access</div>
      <div style={groupBox}>
        {isAdmin && row('Rights', 'Full access to everything (admin)')}
        {usesSiteRole && !isAdmin && row('From role', siteRole
          ? <span>{siteRole.name}{rc && <span className="text-muted"> · {rc.on} of {rc.total} rights</span>}
            <button type="button" onClick={() => { onClose(); navigate('/site-roles'); }} style={{ display: 'block', border: 'none', background: 'none', padding: '4px 0 0', color: 'var(--primary, #2563eb)', fontWeight: 600 }}>View role</button></span>
          : <span style={{ color: 'var(--danger, #dc2626)' }}>No role, cannot open anything</span>)}
        {usesField && !isAdmin && row('Set for this user', `${grantedFieldRights(user)} field right${grantedFieldRights(user) === 1 ? '' : 's'} (agent app)`)}
      </div>

      <div style={sectionLabel}>Actions</div>
      <div style={groupBox}>
        <SheetRow icon={Pencil} label="Edit details and access" onClick={() => setEditOpen(true)} />
        <SheetRow icon={KeyRound} label="Reset password" onClick={() => setPwOpen(true)} />
        {user.isActive
          ? <SheetRow icon={UserX} label={isSelf ? 'Deactivate (not your own account)' : 'Deactivate'} danger disabled={isSelf} onClick={deactivate} />
          : <SheetRow icon={UserCheck} label="Activate" onClick={activate} />}
        <SheetRow icon={Trash2} label="Delete permanently" danger disabled={isSelf} onClick={remove} />
      </div>

      {logs && logs.length > 0 && (
        <>
          <div style={sectionLabel}>Recent changes</div>
          <div style={groupBox}>{logs.map(l => <LogRow key={l._id} l={l} />)}</div>
        </>
      )}
    </AppSheet>
  );
}

// ── Activity tab ────────────────────────────────────────────────────────────
function Activity() {
  const [rows, setRows] = useState(null);
  const [kind, setKind] = useState('all');
  const [q, setQ] = useState('');
  useEffect(() => {
    Promise.all([logsAPI.getAll({ entity: 'user', limit: 150 }), logsAPI.getAll({ entity: 'role', limit: 150 })])
      .then(([u, r]) => setRows([...u.data.data, ...r.data.data].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))))
      .catch(() => { setRows([]); toast.error('Could not load activity (needs access to System Logs)'); });
  }, []);
  const list = (rows || []).filter(l => (kind === 'all' || l.entity === kind)
    && (!q || `${l.customerName} ${l.changedByName} ${l.field}`.toLowerCase().includes(q.toLowerCase())));
  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, overflowX: 'auto' }}>
        {[['all', 'All'], ['user', 'Users'], ['role', 'Roles']].map(([k, l]) => <Chip key={k} active={kind === k} onClick={() => setKind(k)}>{l}</Chip>)}
      </div>
      <SearchBox value={q} onChange={setQ} placeholder="Search name, person or change" />
      {rows === null ? <Spin /> : list.length === 0 ? <Empty text="No activity found." /> : <div style={groupBox}>{list.map(l => <LogRow key={l._id} l={l} showTarget />)}</div>}
    </>
  );
}

const Spin = () => <div style={{ textAlign: 'center', padding: 36 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;
const Empty = ({ text }) => <div className="text-muted" style={{ textAlign: 'center', padding: 32 }}>{text}</div>;
function SearchBox({ value, onChange, placeholder, children }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '0 12px' }}>
        <Search size={16} color="var(--text-muted)" />
        <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} aria-label={placeholder}
          style={{ flex: 1, border: 'none', background: 'none', outline: 'none', color: 'var(--text)', minHeight: 44 }} />
      </div>
      {children}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
export default function UsersMobile() {
  const { user: me } = useAuth();
  const [params] = useSearchParams();
  const tab = params.get('tab') === 'activity' ? 'activity' : 'users';
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [registry, setRegistry] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleF, setRoleF] = useState('all');
  const [status, setStatus] = useState('all');
  const [filterOpen, setFilterOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    try {
      const [u, r, reg] = await Promise.all([userAPI.getAll(), roleAPI.getAll().catch(() => ({ data: { data: [] } })), userAPI.getModuleRegistry().catch(() => ({ data: { data: [] } }))]);
      setUsers(u.data.data); setRoles(r.data.data || []); setRegistry(reg.data.data || []);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const list = useMemo(() => filterUsers(users, { search, role: roleF, status }), [users, search, roleF, status]);
  const counts = useMemo(() => ({ all: users.length, active: users.filter(u => u.isActive).length, inactive: users.filter(u => !u.isActive).length }), [users]);
  const open = users.find(u => u._id === openId);
  const filterCount = (roleF !== 'all' ? 1 : 0);

  return (
    <div>
      <UsersTabs active={tab} />
      {tab === 'activity' ? <Activity /> : (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, overflowX: 'auto' }}>
            <Chip active={status === 'all'} onClick={() => setStatus('all')}>All {counts.all}</Chip>
            <Chip active={status === 'active'} onClick={() => setStatus('active')}>Active {counts.active}</Chip>
            <Chip active={status === 'inactive'} onClick={() => setStatus('inactive')}>Inactive {counts.inactive}</Chip>
          </div>
          <SearchBox value={search} onChange={setSearch} placeholder="Name, mobile or email">
            <button type="button" aria-label="Filter by role" onClick={() => setFilterOpen(true)}
              style={{ width: 46, border: '1px solid var(--border)', borderRadius: 10, background: filterCount ? 'var(--primary, #2563eb)' : 'var(--bg-card)', color: filterCount ? '#fff' : 'var(--text)', display: 'grid', placeItems: 'center' }}>
              <SlidersHorizontal size={18} />
            </button>
          </SearchBox>

          {loading ? <Spin /> : list.length === 0 ? <Empty text={users.length ? 'No users match.' : 'No users yet.'} /> : (
            <div style={groupBox}>
              {list.map(u => (
                <button key={u._id} type="button" onClick={() => setOpenId(u._id)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, minHeight: 64, padding: '8px 14px', border: 'none', borderBottom: '1px solid var(--border)', background: 'none', textAlign: 'left', color: 'var(--text)', cursor: 'pointer', opacity: u.isActive ? 1 : .6 }}>
                  <Avatar name={u.name} active={u.isActive} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '.92rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, minWidth: 0 }}>
                      <RoleTag role={u.role} />
                      <span className="text-muted" style={{ fontSize: '.76rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.mobile}</span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: '.72rem', fontWeight: 700, color: u.isActive ? '#059669' : '#d97706' }}>{u.isActive ? 'Active' : 'Inactive'}</div>
                    <div className="text-muted" style={{ fontSize: '.7rem' }}>{u.lastLogin ? ago(u.lastLogin) : 'Never'}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          <button type="button" aria-label="Add user" style={fabStyle} onClick={() => setAdding(true)}><Plus size={26} /></button>
        </>
      )}

      {filterOpen && (
        <AppSheet title="Filter users" onClose={() => setFilterOpen(false)}>
          <div style={{ ...sectionLabel, marginTop: 0 }}>Role</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {['all', ...ROLES].map(r => <Chip key={r} active={roleF === r} onClick={() => setRoleF(r)}>{r === 'all' ? 'All roles' : ROLE_LABEL[r]}</Chip>)}
          </div>
          <button type="button" className="btn btn-primary" style={{ width: '100%', minHeight: 48 }} onClick={() => setFilterOpen(false)}>Show {list.length} user{list.length === 1 ? '' : 's'}</button>
        </AppSheet>
      )}

      {open && <Profile user={open} me={me} roles={roles} registry={registry} askConfirm={setConfirm} onClose={() => setOpenId(null)} onChanged={load} />}
      {adding && <UserForm user={null} me={me} roles={roles} askConfirm={setConfirm} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
      <ConfirmSheet confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
